import React, { useCallback } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Form, Field } from "react-final-form";
import { useTranslation } from "react-i18next";
import { FORM_ERROR } from "final-form";
import {
  TextInput,
  ContainedButton,
  Heading,
  Subheading,
  FormFooterText,
  Link,
  DescriptionText,
  LoadingIndicator
} from "shared/components";
import { Routes } from "config/navigation";
import { FormWrapper } from "../FormWrapper";
import { FormContainer } from "../FormContainer";
import { ErrorText } from "../ErrorText";
import { useAccountEmail, useCreateAccountMutation } from "../../hooks";
import { LoadingWrapper } from "./CreateAccountForm.styles";
import {
  passwordValidator,
  firstnameValidator,
  lastnameValidator,
  emailValidator
} from "./CreateAccountForm.validation";
import { createInitialState } from "./CreateAccountForm.helpers";

export const CreateAccountForm = () => {
  const { t } = useTranslation();

  const { key } = useParams<{ key: string }>();

  const { email, valid, loading } = useAccountEmail(key);

  const submit = useCreateAccountMutation(key);

  const navigate = useNavigate();

  const handleClickLogin = useCallback(() => {
    navigate(Routes.LOGIN);
  }, []);

  if (loading) {
    return (
      <LoadingWrapper>
        <LoadingIndicator />
      </LoadingWrapper>
    );
  }

  if (!valid) {
    return <Navigate to={Routes.REGISTER} />;
  }

  return (
    <FormWrapper>
      <Heading>{t("authModule.createAccountScreen.heading")}</Heading>
      <Subheading>{t("authModule.createAccountScreen.subheading")}</Subheading>
      <DescriptionText>{t("authModule.createAccountScreen.description")}</DescriptionText>
      <FormContainer>
        <Form
          initialValues={createInitialState(email)}
          onSubmit={submit}
          render={({ dirty, handleSubmit, submitting, submitErrors }) => (
            <React.Fragment>
              <Field
                name="firstname"
                validate={firstnameValidator}
                render={({
                  input: { value, onChange, onBlur, onFocus },
                  meta: { error, touched }
                }) => (
                  <TextInput
                    placeholder={t("global.placeholders.firstName")}
                    label={t("authModule.createAccountScreen.form.firstName")}
                    value={value}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    error={error && touched}
                    errorMessage={t(error)}
                  />
                )}
              />
              <Field
                name="lastname"
                validate={lastnameValidator}
                render={({
                  input: { value, onChange, onBlur, onFocus },
                  meta: { error, touched }
                }) => (
                  <TextInput
                    placeholder={t("global.placeholders.lastName")}
                    label={t("authModule.createAccountScreen.form.lastName")}
                    value={value}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    error={error && touched}
                    errorMessage={t(error)}
                  />
                )}
              />
              <Field
                name="email"
                validate={emailValidator}
                render={({ input: { value } }) => (
                  <TextInput
                    placeholder={t("global.placeholders.email")}
                    label={t("authModule.createAccountScreen.form.email")}
                    value={value}
                    disabled
                  />
                )}
              />
              <Field
                name="password"
                validate={passwordValidator}
                render={({
                  input: { value, onChange, onBlur, onFocus },
                  meta: { error, touched }
                }) => (
                  <TextInput
                    password
                    placeholder={t("global.placeholders.password")}
                    label={t("authModule.createAccountScreen.form.password")}
                    value={value}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    error={error && touched}
                    errorMessage={t(error)}
                  />
                )}
              />
              <ContainedButton
                loading={submitting}
                disabled={!dirty}
                type="submit"
                onClick={handleSubmit}
              >
                {t("authModule.createAccountScreen.form.submitButton")}
              </ContainedButton>
              <FormFooterText>
                {t("authModule.createAccountScreen.footerText")}{" "}
                <Link onClick={handleClickLogin} underline="always">
                  {t("authModule.createAccountScreen.links.login")}
                </Link>
              </FormFooterText>
              {submitErrors && <ErrorText>{t(submitErrors[FORM_ERROR])}</ErrorText>}
            </React.Fragment>
          )}
        />
      </FormContainer>
    </FormWrapper>
  );
};
